import { useCallback, useEffect, useMemo, useRef } from "react";

import { DecisionEnvironment } from "src/clients/jobs/api";
import { useJobSources, useJobs, useRuns } from "src/jobs/api/queries";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const useTestRunJob = (flowId: string, flowVersionId: string) => {
  const { workspace } = useWorkspaceContext();
  const { data: jobs, isLoading } = useJobs({
    baseUrl: workspace.base_url!,
    flowId,
    flowVersionId,
    includeTestRuns: true,
  });

  return {
    testRunJob: jobs?.find((job) => job.tags?.test_run === true),
    isLoading,
  };
};

export const useTestRunRuns = (flowId: string, flowVersionId: string) => {
  const { workspace } = useWorkspaceContext();
  const { testRunJob, isLoading: isTestRunJobLoading } = useTestRunJob(
    flowId,
    flowVersionId,
  );

  const noTestRunJob = !isTestRunJobLoading && testRunJob == undefined;

  const {
    data: testRunRuns,
    isLoading: isRunsLoading,
    isPending,
  } = useRuns(workspace.base_url!, testRunJob?.id ?? "", {
    environment: DecisionEnvironment.TEST,
  });

  const { data: sources, isLoading: isSourcesLoading } = useJobSources(
    workspace.base_url!,
    testRunJob?.id ?? "",
  );

  const sourceIdToDatasetId = useMemo(() => {
    return (
      sources?.reduce(
        (acc, source) => {
          if (source && source.configuration.type === "dataset") {
            acc[source.id] = source.configuration.dataset_id;
          }
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {}
    );
  }, [sources]);

  const isRunning = useMemo(() => {
    return (
      testRunRuns?.pages
        .flat()
        .some((run) => run.status?.type === JobRunStatusType.Running) ?? false
    );
  }, [testRunRuns]);

  return {
    isPending,
    isRunning,
    sourceIdToDatasetId,
    isLoading: isTestRunJobLoading || isRunsLoading || isSourcesLoading,
    testRunRuns: noTestRunJob ? [] : testRunRuns?.pages.flat(),
  };
};

export const STOP_TEST_RUN_EVENT = "stop-test-run";

export type StopTestRunEventDetails = {
  source: "sidepane" | "run-button";
};

export const useStopTestRunListener = (
  callback: (event: CustomEvent<StopTestRunEventDetails>) => void,
) => {
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    const handler = (event: Event) => {
      callbackRef.current(
        event as unknown as CustomEvent<StopTestRunEventDetails>,
      );
    };

    document.addEventListener(STOP_TEST_RUN_EVENT, handler);

    return () => {
      document.removeEventListener(STOP_TEST_RUN_EVENT, handler);
    };
  }, []);
};

export const useDispatchStopTestRunEvent = () => {
  return useCallback((source: StopTestRunEventDetails["source"]) => {
    document.dispatchEvent(
      new CustomEvent<StopTestRunEventDetails>(STOP_TEST_RUN_EVENT, {
        detail: { source },
      }),
    );
  }, []);
};

export const LOAD_TEST_RUN_EVENT = "load-test-run";

export type LoadTestRunEventDetails = {
  jobRun: JobRun;
  datasetId: string;
  datasetEtag: string;
};

export const useLoadTestRunListener = (
  callback: (event: CustomEvent<LoadTestRunEventDetails>) => void,
) => {
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    const handler = (event: Event) => {
      callbackRef.current(
        event as unknown as CustomEvent<LoadTestRunEventDetails>,
      );
    };

    document.addEventListener(LOAD_TEST_RUN_EVENT, handler);

    return () => {
      document.removeEventListener(LOAD_TEST_RUN_EVENT, handler);
    };
  }, []);
};

export const useDispatchLoadTestRunEvent = () => {
  return useCallback((detail: LoadTestRunEventDetails) => {
    document.dispatchEvent(
      new CustomEvent<LoadTestRunEventDetails>(LOAD_TEST_RUN_EVENT, {
        detail,
      }),
    );
  }, []);
};
